import { Fragment, useEffect, useState, useRef } from "react";
import { api, formatTL } from "@/lib/api";
import { printReceiptFrame } from "@/lib/receipt";
import { toast } from "sonner";
import { Clock, Printer, Download, ChevronDown, Bell, BellOff } from "lucide-react";
import { ringBell, isBellOn, setBellOn } from "@/lib/bell";

const STATUSES = ["Bekliyor", "Hazırlanıyor", "Teslim Edildi"];
const OVERDUE_SECS = 1200;
const STATUS_STYLE = {
  Bekliyor: "bg-amber-100 text-amber-700 border-amber-200",
  Hazırlanıyor: "bg-blue-100 text-blue-700 border-blue-200",
  "Teslim Edildi": "bg-green-100 text-green-700 border-green-200",
};

const dayKey = (o) => new Date(o.delivered_at || o.created_at).toLocaleDateString("sv-SE");
const dayLabel = (k) => {
  const today = new Date().toLocaleDateString("sv-SE");
  const y = new Date(Date.now() - 86400000).toLocaleDateString("sv-SE");
  if (k === today) return "Bugün";
  if (k === y) return "Dün";
  return new Date(k + "T12:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "long", weekday: "long" });
};

export const OrderBoard = () => {
  const [orders, setOrders] = useState([]);
  const [openDays, setOpenDays] = useState(() => new Set([new Date().toLocaleDateString("sv-SE")]));
  const [delFilter, setDelFilter] = useState("7");
  const [delDay, setDelDay] = useState(new Date().toLocaleDateString("sv-SE"));
  const inDelRange = (o) => {
    const k = dayKey(o);
    if (delFilter === "all") return true;
    if (delFilter === "day") return k === delDay;
    const from = new Date(Date.now() - (Number(delFilter) - 1) * 86400000).toLocaleDateString("sv-SE");
    return k >= from;
  };
  const toggleDay = (k) =>
    setOpenDays((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  const [now, setNow] = useState(Date.now());
  const [bellOn, setBell] = useState(isBellOn());
  const alertedRef = useRef(new Set());
  const printedRef = useRef(new Set());
  const baselineRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      osc.start();
      osc.stop(ctx.currentTime + 0.35);
      setTimeout(() => ctx.close(), 600);
    } catch (e) {}
  };

  useEffect(() => {
    orders.forEach((o) => {
      if (o.order_type !== "Paket Servis" || o.status === "Teslim Edildi") return;
      const secs = (now - new Date(o.created_at).getTime()) / 1000;
      if (secs >= OVERDUE_SECS && !alertedRef.current.has(o.id)) {
        alertedRef.current.add(o.id);
        playBeep();
        toast.warning(`#${o.order_number} paket servis gecikti!`);
      }
    });
  }, [now, orders]);

  const fmtElapsed = (secs) => {
    secs = Math.max(0, Math.floor(secs));
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  };

  const load = async () => {
    try {
      const res = await api.get("/orders");
      const data = res.data;
      setOrders(data);
      const ts = (o) => new Date(o.created_at).getTime() || 0;
      const maxTs = data.reduce((m, o) => Math.max(m, ts(o)), 0);
      // İlk yüklemede en yeni siparişin zamanını referans al; eski siparişler asla yazdırılmaz (no 300'de başa döndüğü için numara değil zaman kullanılır)
      if (baselineRef.current === null) {
        baselineRef.current = maxTs;
        return;
      }
      data.forEach((o) => {
        if (
          o.order_type !== "Paket Servis" &&
          ts(o) > baselineRef.current &&
          !printedRef.current.has(o.id)
        ) {
          printedRef.current.add(o.id);
          ringBell();
          toast.success(`Yeni ${o.order_type === "Link" ? "online" : "kiosk"} sipariş #${o.order_number} — mutfağa yazdırılıyor`);
          printReceiptFrame(o, "kitchen");
        }
      });
    } catch (e) {
      toast.error("Siparişler yüklenemedi");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      load();
    } catch (e) {
      toast.error("Durum güncellenemedi");
    }
  };

  const exportCsv = () => {
    const headers = ["Sipariş No", "Tarih", "Tip", "Platform", "Durum", "Ödeme", "Müşteri", "Telefon", "Adres", "Ürünler", "Tutar (TL)"];
    const esc = (v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`;
    const rows = orders.map((o) => [
      o.order_number,
      o.created_at ? new Date(o.created_at).toLocaleString("tr-TR") : "",
      o.order_type || "",
      o.platform || "",
      o.status || "",
      o.payment_method || "",
      o.customer_name || "",
      o.customer_phone || "",
      o.customer_address || "",
      (o.items || []).map((it) => `${it.quantity}x ${it.name}${it.note ? ` (${it.note})` : ""}`).join(", "),
      Number(o.total || 0).toFixed(2),
    ]);
    const csv = [headers, ...rows].map((r) => r.map(esc).join(";")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `siparisler-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (orders.length === 0) {
    return (
      <div
        data-testid="orders-empty"
        className="bg-white rounded-2xl border border-[var(--fb-border)] p-10 text-center text-[var(--fb-muted)]"
      >
        Henüz sipariş yok.
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <p className="text-sm text-[var(--fb-muted)]">Toplam {orders.length} sipariş</p>
        <div className="flex items-center gap-2 flex-wrap">
        <button
          data-testid="bell-toggle-btn"
          onClick={() => {
            setBellOn(!bellOn);
            setBell(!bellOn);
            if (!bellOn) ringBell(true);
          }}
          className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border transition-colors active:scale-95 ${bellOn ? "bg-amber-50 border-amber-400 text-amber-700" : "border-[var(--fb-border)] text-[var(--fb-muted)]"}`}
        >
          {bellOn ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />} Zil {bellOn ? "Açık" : "Kapalı"}
        </button>
        <button
          data-testid="bell-test-btn"
          onClick={() => ringBell(true)}
          className="text-sm font-semibold px-4 py-2 rounded-full border border-[var(--fb-border)] hover:bg-[var(--fb-neutral)] active:scale-95"
        >
          Zili Test Et
        </button>
        <button
          data-testid="export-orders-csv-btn"
          onClick={exportCsv}
          className="flex items-center gap-1.5 text-sm font-semibold text-[var(--fb-red)] border border-[var(--fb-red)] hover:bg-red-50 px-4 py-2 rounded-full transition-colors active:scale-95"
        >
          <Download className="w-4 h-4" /> CSV / Excel İndir
        </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {STATUSES.map((col) => (
        <div key={col} className="space-y-3">
          <h3 className="font-display font-bold flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${STATUS_STYLE[col].split(" ")[0]}`} />
            {col}
            <span className="text-sm text-[var(--fb-muted)]">
              ({orders.filter((o) => o.status === col && (col !== "Teslim Edildi" || inDelRange(o))).length})
            </span>
          </h3>
          {col === "Teslim Edildi" && (
            <div data-testid="delivered-filter" className="flex flex-wrap items-center gap-1.5">
              {[
                ["1", "Bugün"],
                ["7", "Son 7 gün"],
                ["30", "Son 30 gün"],
                ["all", "Tümü"],
                ["day", "Tarih seç"],
              ].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`delivered-filter-${v}`}
                  onClick={() => {
                    setDelFilter(v);
                    if (v === "day") setOpenDays((s) => new Set([...s, delDay]));
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                    delFilter === v ? "bg-green-700 text-white border-green-700" : "bg-white border-[var(--fb-border)] text-[var(--fb-muted)] hover:border-green-700"
                  }`}
                >
                  {l}
                </button>
              ))}
              {delFilter === "day" && (
                <input
                  type="date"
                  data-testid="delivered-filter-date"
                  value={delDay}
                  onChange={(e) => {
                    setDelDay(e.target.value);
                    setOpenDays((s) => new Set([...s, e.target.value]));
                  }}
                  className="bg-white border border-[var(--fb-border)] rounded-full px-3 py-1 text-xs font-semibold"
                />
              )}
            </div>
          )}
          {col === "Teslim Edildi" && !orders.some((o) => o.status === col && inDelRange(o)) && (
            <p data-testid="delivered-empty" className="text-sm text-[var(--fb-muted)] px-1">Bu aralıkta teslim edilen sipariş yok.</p>
          )}
          {orders
            .filter((o) => o.status === col && (col !== "Teslim Edildi" || inDelRange(o)))
            .sort((a, b) => (col === "Teslim Edildi" ? new Date(b.delivered_at || b.created_at) - new Date(a.delivered_at || a.created_at) : 0))
            .map((o, i, arr) => {
              const dk = dayKey(o);
              const grouped = col === "Teslim Edildi";
              const header =
                grouped && (i === 0 || dayKey(arr[i - 1]) !== dk) ? (
                  <button
                    data-testid={`delivered-day-${dk}`}
                    onClick={() => toggleDay(dk)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-green-50 border border-green-200 font-bold text-sm text-green-800"
                  >
                    <span>{dayLabel(dk)} · {arr.filter((x) => dayKey(x) === dk).length} sipariş · {formatTL(arr.filter((x) => dayKey(x) === dk).reduce((a, x) => a + (x.total || 0), 0))}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${openDays.has(dk) ? "rotate-180" : ""}`} />
                  </button>
                ) : null;
              if (grouped && !openDays.has(dk)) return header ? <Fragment key={`h-${dk}`}>{header}</Fragment> : null;
              const isPaket = o.order_type === "Paket Servis";
              const delivered = o.status === "Teslim Edildi";
              const start = new Date(o.created_at).getTime();
              const endT = delivered && o.delivered_at ? new Date(o.delivered_at).getTime() : now;
              const secs = (endT - start) / 1000;
              const overdue = isPaket && !delivered && secs >= OVERDUE_SECS;
              const timerColor =
                delivered || secs < 600
                  ? "bg-green-100 text-green-700 border-green-200"
                  : secs < OVERDUE_SECS
                  ? "bg-amber-100 text-amber-700 border-amber-200"
                  : "bg-red-100 text-red-700 border-red-200";
              return (
              <Fragment key={o.id}>
              {header}
              <div
                data-testid={`order-card-${o.order_number}`}
                className={`bg-white rounded-2xl border p-4 ${overdue ? "fb-blink" : "border-[var(--fb-border)]"}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-display font-extrabold text-lg text-[var(--fb-red)]">
                    #{o.order_number}
                  </span>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${STATUS_STYLE[o.status]}`}
                    >
                      {o.order_type}
                    </span>
                    {isPaket && o.platform && (
                      <span
                        data-testid={`order-platform-${o.order_number}`}
                        className="text-[11px] font-semibold text-[var(--fb-muted)]"
                      >
                        {o.platform}
                      </span>
                    )}
                  </div>
                </div>
                <div
                  data-testid={`order-timer-${o.order_number}`}
                  className={`mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${timerColor}`}
                >
                  <Clock className="w-3.5 h-3.5" />
                  {delivered ? `Teslim süresi: ${fmtElapsed(secs)}` : fmtElapsed(secs)}
                </div>
                <ul className="mt-2 text-sm space-y-0.5">
                  {o.items.map((it, idx) => (
                    <li key={idx} className="flex justify-between text-[var(--fb-muted)]">
                      <span>
                        {it.quantity}× {it.name}
                        {it.note ? ` (${it.note})` : ""}
                      </span>
                      <span>{formatTL(it.price * it.quantity)}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--fb-border)]">
                  <span className="font-display font-bold">{formatTL(o.total)}</span>
                  <span className="text-xs text-[var(--fb-muted)]">{o.payment_method}</span>
                </div>
                {o.discount_amount > 0 && (
                  <p data-testid={`order-discount-${o.order_number}`} className="text-xs font-semibold text-green-700 mt-1">
                    İndirim ({o.discount_code}): -{formatTL(o.discount_amount)}
                  </p>
                )}
                <button
                  data-testid={`print-order-${o.order_number}`}
                  onClick={() => printReceiptFrame(o, "kitchen")}
                  title="Mutfak fişi yazdır"
                  className="w-full mt-2 flex items-center justify-center gap-2 text-sm font-bold px-4 py-2.5 rounded-full bg-[var(--fb-ink)] text-white hover:bg-black active:scale-[0.98] transition-colors"
                >
                  <Printer className="w-4 h-4" /> Mutfak Fişi
                </button>
                {!delivered && (
                  <div className="flex gap-2 mt-3">
                    {STATUSES.filter((s) => s !== o.status).map((s) => (
                      <button
                        key={s}
                        data-testid={`set-status-${o.order_number}-${s}`}
                        onClick={() => setStatus(o.id, s)}
                        className="flex-1 text-xs font-semibold py-2 rounded-full border border-[var(--fb-border)] hover:border-[var(--fb-red)] hover:text-[var(--fb-red)] transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              </Fragment>
              );
            })}
        </div>
      ))}
      </div>
    </>
  );
};
